// Qui paie cette formation, et où en est l'accord.
//
// Rien ne le disait : les inscriptions ne portaient que leur date, et on
// découvrait la prise en charge — ou son absence — le jour de la facture.
//
// L'état se déduit des dates posées ; c'est le serveur qui le dit, et l'écran
// ne le recalcule pas : deux calculs du même état finiraient par ne pas dire
// la même chose sur la même ligne, et celui-là décide d'une facture.
import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, Plus, Trash2, AlertTriangle, Check, Clock, X } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { messageErreur } from '../lib/erreur';

const ETAT = {
  a_monter: 'bg-[#F6F7FB] text-[#6B6C85]',
  depose: 'bg-[#FEF3E7] text-[#B45309]',
  accorde: 'bg-[#E6FBF7] text-[#0F766E]',
  refuse: 'bg-[#FEF6F6] text-[#B91C1C]',
  solde: 'bg-[#EEF2FF] text-[#4F46E5]',
};

const euros = (n) => (typeof n === 'number' && n
  ? n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
  : '—');

const jourFr = (v) => {
  if (!v) return '';
  const d = new Date(String(v).slice(0, 10));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};

const VIDE = {
  financeur_id: '', financeur_nom: '', numero: '', apprenants: [],
  montant_demande: 0, montant_accorde: 0, subrogation: false, depose_par: 'nous',
  depose_le: '', accord_le: '', refus_le: '', solde_le: '', motif_refus: '',
};

export default function PanneauFinancement({ sessionId }) {
  const [etat, setEtat] = useState(null);
  const [ouvert, setOuvert] = useState(null); // le dossier en cours d'édition
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}/financements`);
      setEtat(data);
    } catch { /* un panneau muet vaut mieux qu'une page cassée */ }
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  if (!etat) return null;
  const { financements = [], couverture = {}, apprenants = [], resume = {},
          financeurs = [], total } = etat;
  const sansAccord = couverture.sans_accord || [];

  const enregistrer = async (d) => {
    setOccupe(true);
    try {
      const corps = {
        ...d,
        montant_demande: Number(d.montant_demande) || 0,
        montant_accorde: Number(d.montant_accorde) || 0,
        apprenants: d.apprenants || [],
        depose_le: d.depose_le || null,
        accord_le: d.accord_le || null,
        refus_le: d.refus_le || null,
        solde_le: d.solde_le || null,
      };
      if (d.id) await api.put(`/financements/${d.id}`, corps);
      else await api.post(`/sessions/${sessionId}/financements`, corps);
      setOuvert(null);
      await charger();
      toast({ title: 'Prise en charge enregistrée' });
    } catch (err) {
      toast({ title: 'Enregistrement refusé', description: messageErreur(err), variant: 'destructive' });
    } finally { setOccupe(false); }
  };

  const retirer = async (d) => {
    try {
      await api.delete(`/financements/${d.id}`);
      await charger();
    } catch (err) {
      toast({ title: 'Impossible de retirer', description: messageErreur(err), variant: 'destructive' });
    }
  };

  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5 my-3"
         data-testid={`financement-${sessionId}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6C85] flex items-center gap-1.5">
          <Banknote className="w-3.5 h-3.5" /> Qui paie
        </div>
        <button
          onClick={() => setOuvert({ ...VIDE })}
          data-testid="financement-nouveau"
          className="h-7 px-2.5 rounded-lg bg-white border border-[#EFF0F6] text-[11.5px] font-semibold text-[#4F46E5] hover:border-[#C7D2FE] inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Prise en charge
        </button>
      </div>

      {financements.length === 0 ? (
        <div className="text-[12px] text-[#8E8FA5]">
          {apprenants.length > 0
            ? `Personne ne dit qui paie — ${apprenants.length} inscrit${apprenants.length > 1 ? 's' : ''} sur cette formation.`
            : 'Aucune prise en charge.'}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {financements.map((d) => (
            <li key={d.id} className="flex items-center gap-2.5 bg-white rounded-lg border border-[#EFF0F6] px-2.5 py-2">
              <button
                onClick={() => setOuvert({ ...VIDE, ...d, apprenants: d.apprenants || [] })}
                data-testid={`financement-${d.id}`}
                className="flex-1 min-w-0 text-left"
              >
                <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">
                  {d.financeur_nom || 'Financeur à nommer'}
                  {d.numero && <span className="font-normal text-[#8E8FA5]"> · n° {d.numero}</span>}
                </div>
                <div className="text-[11px] text-[#8E8FA5] truncate">
                  {euros(d.montant_accorde || d.montant_demande)}
                  {d.apprenants?.length ? ` · ${d.apprenants.length} apprenant(s)` : ' · toute la formation'}
                  {d.accord_le ? ` · accord du ${jourFr(d.accord_le)}` : ''}
                  {d.subrogation ? ' · subrogation' : ''}
                </div>
                {typeof d.reste_au_client === 'number' && d.reste_au_client > 0 && (
                  <div className="text-[11px] text-[#B45309]">
                    {euros(d.montant_accorde)} pris en charge — {euros(d.reste_au_client)} restent
                    à la charge du client.
                  </div>
                )}
                {d.qui_paie && (
                  <div className="text-[10.5px] text-[#6B6C85] truncate">{d.qui_paie.pourquoi}</div>
                )}
              </button>
              {d.a_relancer && (
                <span className="shrink-0 text-[10.5px] font-semibold text-[#B45309]"
                      title="Déposé depuis plus de trois semaines">
                  {d.qui_relance === 'client' ? 'à redemander au client' : 'à relancer'}
                </span>
              )}
              <span className={`shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-md ${ETAT[d.etat] || ''}`}>
                {d.etat_libelle}
              </span>
              {d.etat === 'a_monter' && (
                <button
                  onClick={() => retirer(d)}
                  title="Retirer ce dossier"
                  className="shrink-0 w-7 h-7 rounded-lg border border-[#EFF0F6] text-[#8E8FA5] flex items-center justify-center hover:text-[#B91C1C] hover:border-[#FECACA]"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {typeof total === 'number' && total > 0 && financements.length > 0 && (
        <div className="mt-2 text-[11px] text-[#8E8FA5]">
          Prix de la formation : {euros(total)}.
        </div>
      )}

      {sansAccord.length > 0 && financements.length > 0 && (
        <div className="mt-2 text-[11.5px] text-[#B45309] flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            {sansAccord.length} inscrit{sansAccord.length > 1 ? 's' : ''} sur {couverture.total} ne
            {sansAccord.length > 1 ? ' sont' : " n'est"} couvert{sansAccord.length > 1 ? 's' : ''} par
            aucun accord — un dossier déposé est une demande, pas une prise en charge.
          </span>
        </div>
      )}

      {resume.a_relancer > 0 && (
        <div className="mt-1.5 text-[11.5px] text-[#B45309] flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {resume.a_relancer} dossier{resume.a_relancer > 1 ? 's' : ''} déposé
          {resume.a_relancer > 1 ? 's' : ''} sans réponse depuis plus de trois semaines.
        </div>
      )}

      {ouvert && (
        <Fenetre
          valeur={ouvert}
          financeurs={financeurs}
          apprenants={apprenants}
          occupe={occupe}
          onFermer={() => setOuvert(null)}
          onEnregistrer={enregistrer}
        />
      )}
    </div>
  );
}

function Fenetre({ valeur, financeurs, apprenants, occupe, onFermer, onEnregistrer }) {
  const [v, setV] = useState(valeur);
  useEffect(() => { setV(valeur); }, [valeur]);
  const set = (k, x) => setV((s) => ({ ...s, [k]: x }));
  const manque = (champ) => (valeur.manques || []).find((m) => m.champ === champ);

  const basculer = (id) => setV((s) => {
    const liste = s.apprenants || [];
    return { ...s, apprenants: liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id] };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
         onClick={onFermer}>
      <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[88vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-4 pb-3 border-b border-[#EFF0F6] flex items-center justify-between">
          <div className="text-[15px] font-bold text-[#1A1B2E]"
               style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {v.id ? 'Prise en charge' : 'Nouvelle prise en charge'}
          </div>
          <button onClick={onFermer} className="w-8 h-8 rounded-lg text-[#8E8FA5] hover:bg-[#F6F7FB] flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3.5">
          <Champ label="Le financeur">
            <select
              value={v.financeur_id || ''}
              data-testid="financement-financeur"
              onChange={(e) => {
                const f = financeurs.find((x) => x.id === e.target.value);
                setV((s) => ({ ...s, financeur_id: e.target.value, financeur_nom: f?.name || '' }));
              }}
              className={cls}
            >
              <option value="">— Choisir —</option>
              {financeurs.some((f) => f.propose) && (
                <optgroup label="Proposés pour cette formation">
                  {financeurs.filter((f) => f.propose).map((f) => (
                    <option key={f.id} value={f.id}>{f.name} — {f.pourquoi}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Tous les financeurs">
                {financeurs.filter((f) => !f.propose).map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </optgroup>
            </select>
            <Signal m={manque('financeur')} />
          </Champ>

          <div className="grid grid-cols-2 gap-3">
            <Champ label="Montant demandé (€)">
              <input type="number" min={0} value={v.montant_demande || 0}
                     onChange={(e) => set('montant_demande', e.target.value)} className={cls} />
              <Signal m={manque('montant_demande')} />
            </Champ>
            <Champ label="Montant accordé (€)">
              <input type="number" min={0} value={v.montant_accorde || 0}
                     onChange={(e) => set('montant_accorde', e.target.value)} className={cls} />
              <Signal m={manque('montant_accorde')} />
            </Champ>
            <Champ label="N° d'accord">
              <input value={v.numero || ''} onChange={(e) => set('numero', e.target.value)}
                     placeholder="Celui du financeur" className={cls} />
              <Signal m={manque('numero')} />
            </Champ>
            <Champ label="Déposé le">
              <input type="date" value={(v.depose_le || '').slice(0, 10)}
                     onChange={(e) => set('depose_le', e.target.value)} className={cls} />
            </Champ>
            <Champ label="Accord le">
              <input type="date" value={(v.accord_le || '').slice(0, 10)}
                     onChange={(e) => set('accord_le', e.target.value)} className={cls} />
            </Champ>
            <Champ label="Refus le">
              <input type="date" value={(v.refus_le || '').slice(0, 10)}
                     onChange={(e) => set('refus_le', e.target.value)} className={cls} />
            </Champ>
          </div>

          {v.refus_le && (
            <Champ label="Motif du refus">
              <input value={v.motif_refus || ''} onChange={(e) => set('motif_refus', e.target.value)}
                     placeholder="« Pourquoi nos dossiers sont-ils refusés ? » ne se répond pas sans lui"
                     className={cls} />
              <Signal m={manque('motif_refus')} />
            </Champ>
          )}

          <Champ label="Qui a déposé la demande">
            <select value={v.depose_par || 'nous'} data-testid="financement-depose-par"
                    onChange={(e) => set('depose_par', e.target.value)} className={cls}>
              <option value="nous">Nous — le centre de formation</option>
              <option value="client">Le client</option>
            </select>
            <div className="text-[11px] text-[#8E8FA5] mt-1">
              Ça ne change pas l&rsquo;instruction, ça change qui relance : un dossier déposé par
              le client se redemande à lui, pas au financeur.
            </div>
          </Champ>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={!!v.subrogation} data-testid="financement-subrogation"
                   onChange={(e) => set('subrogation', e.target.checked)} className="mt-0.5" />
            <span className="text-[12.5px] text-[#1A1B2E]">
              Subrogation — le financeur règle directement
              <span className="block text-[11px] text-[#8E8FA5]">
                C&rsquo;est la seule chose qui change à qui part la facture. Sans elle, le client
                paie et se fait rembourser.
              </span>
            </span>
          </label>

          {apprenants.length > 0 && (
            <div>
              <div className="text-[11.5px] font-semibold text-[#6B6C85] mb-1">Qui est couvert</div>
              <div className="text-[11px] text-[#8E8FA5] mb-1.5">
                Rien de coché : l&rsquo;accord couvre toute la formation.
              </div>
              <div className="flex flex-wrap gap-1.5">
                {apprenants.map((a) => {
                  const pris = (v.apprenants || []).includes(a.id);
                  return (
                    <button
                      key={a.id} onClick={() => basculer(a.id)}
                      className={`h-7 px-2.5 rounded-lg text-[11.5px] font-medium border ${
                        pris ? 'bg-[#EEF2FF] border-[#C7D2FE] text-[#4F46E5]'
                             : 'bg-white border-[#EFF0F6] text-[#6B6C85]'}`}
                    >
                      {pris && <Check className="w-3 h-3 inline mr-1" />}{a.nom}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#EFF0F6] bg-[#FAFAFE] flex justify-end gap-2">
          <button onClick={onFermer}
                  className="h-9 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[12.5px] font-semibold text-[#6B6C85]">
            Fermer
          </button>
          <button onClick={() => onEnregistrer(v)} disabled={occupe}
                  data-testid="financement-enregistrer"
                  className="h-9 px-3.5 rounded-xl brand-gradient text-white text-[12.5px] font-semibold disabled:opacity-50">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

const cls = 'w-full h-9 px-2.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none';

function Champ({ label, children }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold text-[#6B6C85] mb-1">{label}</label>
      {children}
    </div>
  );
}

// Ce qui manque se dit là où ça se corrige, et il dit pourquoi. Rien ne
// bloque : on monte un dossier à neuf heures et on obtient le numéro à quinze.
function Signal({ m }) {
  if (!m) return null;
  return <div className="text-[11px] text-[#B45309] mt-1">{m.motif}</div>;
}
